import { ApiRequest } from './requests';
import { CueResponse } from '../models/common';

export interface ExchangeLoginPhoenixRequestBM {
  code: string;
  id: string;
  pairingUID?: string;
}

export interface ExchangeLoginPhoenixResponseBM {
  id: string;
  email: string;
  username: string;
  token: string;
  refreshToken: string;
}

export interface LoginPhoenixRequestBM {
  email: string;
  pairingUID?: string;
}

export interface UserHasPasswordRequestBM {
  email: string;
}

export interface UserHasPasswordResponseBM {
  hasPassword: boolean;
}

export interface UserLoginRequestBM {
  email: string;
  password: string;
}

export interface UserForgottenPasswordRequestBM {
  email: string;
}

export interface UserResetPasswordRequestBM {
  email: string;
  token: string;
  password: string;
}

export interface SetUserPasswordRequestBM {
  oldPassword?: string;
  password: string;
}

export interface LinkedAccountResponseBM {
  id: string;
  alias: string;
  pairedDate: string;
  connectionAgentId: number | null;
  email: string | null;
  username: string | null;
  displayName: string | null;
  givenName: string | null;
  surname: string | null;
  primary: boolean;
  profilePhotoUrl: string | null;
  lastActive: string | null;
}

export type ProfilePhotoSource = 'none' | 'linkedAccount' | 'custom';

export interface SetProfilePhotoSourceRequestBM {
  source: ProfilePhotoSource;
  linkedAccountId: string | null;
}

export interface ProfilePhotoResponseBM {
  profilePhotoUrl: string;
}

export interface UnpairLinkedAccountsResponseBM {
  token: string;
  refreshToken: string;
}

export function emailLogin(email: string, pairingUid?: string): ApiRequest<LoginPhoenixRequestBM, string> {
  return {
    url: '/api/account/email-login',
    payload: {
      email: email,
      pairingUID: pairingUid,
    },
    method: 'post',
  };
}

export function unpairLinkedAccounts(id: string): ApiRequest<void, UnpairLinkedAccountsResponseBM | null> {
  return {
    url: '/api/account/linked-accounts/' + id,
    method: 'delete',
  };
}

export function getLinkedAccounts(): ApiRequest<any, LinkedAccountResponseBM[]> {
  return {
    url: '/api/account/linked-accounts',
    method: 'get',
  };
}

export function setPrimaryLinkedAccount(id: string, primary = true): ApiRequest<void, void> {
  return {
    url: `/api/account/linked-accounts/${id}/primary`,
    method: primary ? 'put' : 'delete',
  };
}

export interface UserPinResponseBM {
  pinCode: number;
  token: string;
}

export function requestUserPin(): ApiRequest<void, UserPinResponseBM> {
  return {
    url: '/api/account/pin-request',
    method: 'post',
  };
}

export function confirmUserPin(payload: UserPinResponseBM): ApiRequest<UserPinResponseBM, void> {
  return {
    url: '/api/account/pin-request',
    payload,
    method: 'put',
  };
}

export function deleteUserPin(): ApiRequest<void, void> {
  return {
    url: '/api/account/pin',
    method: 'delete',
  };
}

export function setProfilePhotoSource(
  request: SetProfilePhotoSourceRequestBM
): ApiRequest<SetProfilePhotoSourceRequestBM, void> {
  return {
    url: '/api/account/profile-photo/source',
    payload: request,
    method: 'put',
  };
}

export function uploadProfilePhoto(file: File): ApiRequest<FormData, ProfilePhotoResponseBM> {
  const payload = new FormData();
  payload.append('file', file);

  return {
    url: '/api/account/profile-photo',
    payload,
    method: 'post',
  };
}

export function removeProfilePhoto(): ApiRequest<void, void> {
  return {
    url: '/api/account/profile-photo',
    method: 'delete',
  };
}

export function exchangeEmailLogin(
  request: ExchangeLoginPhoenixRequestBM
): ApiRequest<ExchangeLoginPhoenixRequestBM, ExchangeLoginPhoenixResponseBM> {
  return {
    url: '/api/account/exchange-email-login',
    payload: request,
    method: 'post',
  };
}

export function userLogin(
  request: UserLoginRequestBM
): ApiRequest<{ data: UserLoginRequestBM }, CueResponse<{ token: string; refreshToken: string }>> {
  return {
    url: '/api/account/user-login',
    payload: {
      data: request,
    },
    method: 'post',
  };
}

export function userHasPassword(
  request: UserHasPasswordRequestBM
): ApiRequest<{ data: UserHasPasswordRequestBM }, CueResponse<UserHasPasswordResponseBM>> {
  return {
    url: '/api/account/user-has-password',
    payload: {
      data: request,
    },
    method: 'post',
  };
}

export function userForgottenPassword(
  request: UserForgottenPasswordRequestBM
): ApiRequest<{ data: UserForgottenPasswordRequestBM }, CueResponse<void>> {
  return {
    url: '/api/account/user-forgotten-password',
    payload: {
      data: request,
    },
    method: 'post',
  };
}

export function userPasswordOptions(): ApiRequest<void, { hasPassword: boolean; canDeletePassword: boolean }> {
  return { url: '/api/account/user-password-options', method: 'get' };
}

export function deleteUserPassword(currentPassword: string): ApiRequest<{ currentPassword: string }, void> {
  return { url: '/api/account/user-password', method: 'delete', payload: { currentPassword } };
}

export function userResetPassword(
  request: UserResetPasswordRequestBM
): ApiRequest<{ data: UserResetPasswordRequestBM }, CueResponse<void>> {
  return {
    url: '/api/account/user-reset-password',
    payload: {
      data: request,
    },
    method: 'post',
  };
}

export function setUserPassword(
  request: SetUserPasswordRequestBM
): ApiRequest<{ data: SetUserPasswordRequestBM }, CueResponse<void>> {
  return {
    url: '/api/account/set-user-password',
    payload: {
      data: request,
    },
    method: 'post',
  };
}
